import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  Button,
  Chip,
  Avatar,
  Stack,
  Grid,
  Card,
  CardContent,
  alpha,
  TextField,
  Autocomplete
} from '@mui/material';
import { Wrapper } from '@googlemaps/react-wrapper';
import {
  SettingsEthernet,
  Refresh,
  Circle,
  Map as MapIcon,
  FilterList,
  Search,
  Hub,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { configurationService } from '../../services/configurationService';
import { monitoramentoService, MapaPontoCto } from '../../services/monitoramentoService';
import CtoMonitoramentoDetailsModal from '../../components/modals/CtoMonitoramentoDetailsModal';

const parseCoordinates = (latStr: any, lngStr: any): { lat: number; lng: number } | null => {
  if (latStr === null || lngStr === null || latStr === undefined || lngStr === undefined) return null;
  const lat = parseFloat(latStr);
  const lng = parseFloat(lngStr);

  if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
    return { lat, lng };
  }
  return null;
};

const STATUS_COLORS = {
  online: '#4CAF50',
  offline: '#F44336',
  vazia: '#9E9E9E'
} as const;

const getStatusColor = (status: string) => {
  if (status === 'online') return STATUS_COLORS.online;
  if (status === 'offline') return STATUS_COLORS.offline;
  return STATUS_COLORS.vazia;
};

const FILTER_OPTIONS = [
  { key: 'Todos', label: 'Todas', color: undefined },
  { key: 'Online', label: 'Online', color: STATUS_COLORS.online },
  { key: 'Offline', label: 'Offline', color: STATUS_COLORS.offline },
  { key: 'Vazia', label: 'Vazia', color: STATUS_COLORS.vazia }
] as const;

const darkMapStyles: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#1f2933' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#d5dbe3' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#111827' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#4b5563' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#18212b' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#243241' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#173524' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#354252' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#111827' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#4b5563' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f2937' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#263445' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f2537' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9fb3c8' }] }
];

interface MapComponentProps {
  ctos: MapaPontoCto[];
  onCtoClick: (cto: MapaPontoCto) => void;
  isDarkMode: boolean;
  focusTarget?: {
    lat: number;
    lng: number;
    ctoId: number;
    name: string;
    status: string;
    token: number;
  } | null;
}

const createMarkerIcon = (status: string, scale = 9, highlighted = false): google.maps.Symbol => ({
  path: window.google.maps.SymbolPath.CIRCLE,
  fillColor: getStatusColor(status),
  fillOpacity: highlighted ? 1 : 0.92,
  strokeColor: highlighted ? '#FFFFFF' : '#FFFFFF',
  strokeWeight: highlighted ? 3.5 : 2.5,
  scale: highlighted ? 14 : scale
});

interface StatCardProps {
  label: string;
  value: number;
  color?: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color, icon, active, onClick }) => (
  <Card
    elevation={active ? 4 : 0}
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      height: '100%',
      border: '2px solid',
      borderColor: active ? (color || 'primary.main') : 'divider',
      bgcolor: active ? alpha(color || '#2196F3', 0.08) : 'background.paper',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 3,
        borderColor: color || 'primary.main'
      }
    }}
  >
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
            {label}
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ color: color || 'primary.main', lineHeight: 1.2, mt: 0.5 }}>
            {value}
          </Typography>
        </Box>
        <Avatar
          sx={{
            width: 44,
            height: 44,
            bgcolor: alpha(color || '#2196F3', 0.12),
            color: color || 'primary.main'
          }}
        >
          {icon}
        </Avatar>
      </Stack>
    </CardContent>
  </Card>
);

const normalizeStatusLabel = (status?: string) => {
  if (!status) return 'N/A';
  return status
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const MapComponent: React.FC<MapComponentProps> = ({ ctos, onCtoClick, isDarkMode, focusTarget }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersDataRef = useRef<Map<number, { marker: google.maps.Marker; cto: MapaPontoCto }>>(new Map());
  const blinkIntervalsRef = useRef<number[]>([]);
  const highlightRingRef = useRef<google.maps.Marker | null>(null);
  const highlightPulseRef = useRef<number | null>(null);
  const highlightBounceRef = useRef<number | null>(null);
  const highlightInfoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const [center] = useState({ lat: -15.7801, lng: -47.9292 });
  const [zoom] = useState(5);

  const clearHighlightEffects = useCallback(() => {
    highlightRingRef.current?.setMap(null);
    highlightRingRef.current = null;

    if (highlightPulseRef.current) {
      window.clearInterval(highlightPulseRef.current);
      highlightPulseRef.current = null;
    }

    if (highlightBounceRef.current) {
      window.clearTimeout(highlightBounceRef.current);
      highlightBounceRef.current = null;
    }

    highlightInfoWindowRef.current?.close();
    highlightInfoWindowRef.current = null;

    if (window.google?.maps) {
      markersDataRef.current.forEach(({ marker, cto }) => {
        marker.setAnimation(null);
        marker.setZIndex(undefined);
        marker.setIcon(createMarkerIcon(cto.cto_status));
      });
    }
  }, []);

  const applyHighlight = useCallback((target: NonNullable<MapComponentProps['focusTarget']>) => {
    if (!mapInstanceRef.current || !window.google?.maps) return;

    const map = mapInstanceRef.current;
    const entry = markersDataRef.current.get(target.ctoId);
    if (!entry) return;

    const { marker, cto } = entry;
    const statusColor = getStatusColor(cto.cto_status);

    marker.setIcon(createMarkerIcon(cto.cto_status, 9, true));
    marker.setZIndex(window.google.maps.Marker.MAX_ZINDEX + 1);
    marker.setAnimation(window.google.maps.Animation.BOUNCE);

    highlightBounceRef.current = window.setTimeout(() => {
      marker.setAnimation(null);
    }, 2800);

    let pulseScale = 20;
    let growing = true;
    const ringMarker = new window.google.maps.Marker({
      position: { lat: target.lat, lng: target.lng },
      map,
      clickable: false,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: statusColor,
        fillOpacity: 0.18,
        strokeColor: statusColor,
        strokeWeight: 2.5,
        strokeOpacity: 0.7,
        scale: pulseScale
      },
      zIndex: window.google.maps.Marker.MAX_ZINDEX
    });
    highlightRingRef.current = ringMarker;

    highlightPulseRef.current = window.setInterval(() => {
      if (growing) {
        pulseScale += 1.2;
        if (pulseScale >= 32) growing = false;
      } else {
        pulseScale -= 1.2;
        if (pulseScale <= 20) growing = true;
      }

      ringMarker.setIcon({
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: statusColor,
        fillOpacity: 0.1 + (pulseScale - 20) * 0.008,
        strokeColor: statusColor,
        strokeWeight: 2.5,
        strokeOpacity: 0.45 + (32 - pulseScale) * 0.015,
        scale: pulseScale
      });
    }, 90);

    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="padding:2px 4px;text-align:center;">
          <div style="font-weight:800;font-size:13px;color:#111827;">${target.name}</div>
          <div style="font-size:11px;color:#6b7280;margin-top:2px;">CTO selecionada</div>
        </div>
      `
    });
    infoWindow.open(map, marker);
    highlightInfoWindowRef.current = infoWindow;
  }, []);

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom,
      mapTypeId: 'roadmap',
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
      backgroundColor: isDarkMode ? '#111827' : '#ffffff',
      styles: isDarkMode ? darkMapStyles : []
    });

    mapInstanceRef.current = map;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        map.setCenter({ lat: position.coords.latitude, lng: position.coords.longitude });
        map.setZoom(13);
      });
    }

    return () => {
      clearHighlightEffects();
      blinkIntervalsRef.current.forEach(intervalId => window.clearInterval(intervalId));
      blinkIntervalsRef.current = [];
      markersDataRef.current.forEach(({ marker }) => marker.setMap(null));
      markersDataRef.current.clear();
    };
  }, [center, zoom, clearHighlightEffects]);

  useEffect(() => {
    if (!mapInstanceRef.current) return;

    mapInstanceRef.current.setOptions({
      backgroundColor: isDarkMode ? '#111827' : '#ffffff',
      styles: isDarkMode ? darkMapStyles : []
    });
  }, [isDarkMode]);

  useEffect(() => {
    if (!mapInstanceRef.current || !window.google?.maps) return;

    const map = mapInstanceRef.current;

    clearHighlightEffects();
    blinkIntervalsRef.current.forEach(intervalId => window.clearInterval(intervalId));
    blinkIntervalsRef.current = [];
    markersDataRef.current.forEach(({ marker }) => marker.setMap(null));
    markersDataRef.current.clear();

    const bounds = new window.google.maps.LatLngBounds();
    let hasValidPoints = false;

    ctos.forEach(cto => {
      const coords = parseCoordinates(cto.latitude, cto.longitude);
      if (coords) {
        const marker = new window.google.maps.Marker({
          position: coords,
          map,
          title: `CTO: ${cto.name}`,
          icon: createMarkerIcon(cto.cto_status)
        });

        if (cto.cto_status === 'offline') {
          let isVisible = true;
          const intervalId = window.setInterval(() => {
            if (focusTarget?.ctoId === cto.id) return;
            isVisible = !isVisible;
            marker.setOpacity(isVisible ? 1 : 0.2);
          }, 650);
          blinkIntervalsRef.current.push(intervalId);
        }

        marker.addListener('click', () => onCtoClick(cto));

        markersDataRef.current.set(cto.id, { marker, cto });
        bounds.extend(coords);
        hasValidPoints = true;
      }
    });

    if (hasValidPoints && !focusTarget) {
      map.fitBounds(bounds);
      const listener = window.google.maps.event.addListener(map, 'idle', function() {
        if (map.getZoom()! > 18) map.setZoom(18);
        window.google.maps.event.removeListener(listener);
      });
    }
  }, [ctos, onCtoClick, focusTarget, clearHighlightEffects]);

  useEffect(() => {
    if (!mapInstanceRef.current || !window.google?.maps) return;

    clearHighlightEffects();

    if (!focusTarget) return;

    const map = mapInstanceRef.current;
    map.panTo({ lat: focusTarget.lat, lng: focusTarget.lng });
    map.setZoom(17);
    applyHighlight(focusTarget);

    return () => clearHighlightEffects();
  }, [focusTarget, ctos, applyHighlight, clearHighlightEffects]);

  return <Box ref={mapRef} sx={{ width: '100%', height: '100%' }} />;
};

const MonitoramentoCtosPage: React.FC = () => {
  const theme = useTheme();
  const primario = theme.palette.primary.main;
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('Todos');
  const [selectedCto, setSelectedCto] = useState<MapaPontoCto | null>(null);
  const [ctos, setCtos] = useState<MapaPontoCto[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [focusTarget, setFocusTarget] = useState<{
    lat: number;
    lng: number;
    ctoId: number;
    name: string;
    status: string;
    token: number;
  } | null>(null);

  const ctosWithCoords = useMemo(
    () => ctos.filter(c => parseCoordinates(c.latitude, c.longitude)),
    [ctos]
  );

  const stats = useMemo(() => ({
    total: ctosWithCoords.length,
    online: ctosWithCoords.filter(c => c.cto_status === 'online').length,
    offline: ctosWithCoords.filter(c => c.cto_status === 'offline').length,
    vazia: ctosWithCoords.filter(c => c.cto_status === 'vazia').length
  }), [ctosWithCoords]);

  const filteredCtos = useMemo(() => {
    if (activeFilter === 'Todos') return ctosWithCoords;
    if (activeFilter === 'Online') return ctosWithCoords.filter(c => c.cto_status === 'online');
    if (activeFilter === 'Offline') return ctosWithCoords.filter(c => c.cto_status === 'offline');
    if (activeFilter === 'Vazia') return ctosWithCoords.filter(c => c.cto_status === 'vazia');
    return ctosWithCoords;
  }, [ctosWithCoords, activeFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const config = await configurationService.getConfigurationByKey('google_maps_api_key');
      if (config && config.valor) {
        setGoogleMapsApiKey(config.valor);
      } else {
        setError('Chave da API do Google Maps nao configurada no sistema.');
        return;
      }

      const data = await monitoramentoService.getCtos();
      setCtos(data || []);
    } catch (err: any) {
      console.error('Erro ao inicializar mapa:', err);
      setError('Erro ao carregar dados do mapa. ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenCtoDetails = useCallback((cto: MapaPontoCto) => {
    setSelectedCto(cto);
  }, []);

  const handleCloseCtoDetails = useCallback(() => {
    setSelectedCto(null);
  }, []);

  const handleFilterChange = useCallback((filter: string) => {
    setActiveFilter(filter);
    setFocusTarget(null);
  }, []);

  const handleSearchSelect = useCallback((cto: MapaPontoCto | null) => {
    if (!cto) return;

    const coords = parseCoordinates(cto.latitude, cto.longitude);
    if (!coords) return;

    const isVisible = filteredCtos.some(c => c.id === cto.id);
    if (!isVisible) {
      setActiveFilter('Todos');
    }

    setFocusTarget({
      lat: coords.lat,
      lng: coords.lng,
      ctoId: cto.id,
      name: cto.name,
      status: cto.cto_status,
      token: Date.now()
    });
    setSearchInput('');
  }, [filteredCtos]);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 2 }}>
        <CircularProgress size={48} thickness={4} />
        <Typography color="text.secondary" fontWeight={500}>Carregando mapa de CTOs...</Typography>
      </Box>
    );
  }

  if (error || !googleMapsApiKey) {
    return (
      <Box sx={{ p: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Avatar sx={{ width: 56, height: 56, mx: 'auto', mb: 2, bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main' }}>
            <MapIcon />
          </Avatar>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>Monitoramento de CTOs</Typography>
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>{error || 'Chave do Google Maps invalida.'}</Alert>
          <Button variant="contained" onClick={loadData} startIcon={<Refresh />}>Tentar Novamente</Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper
        elevation={2}
        sx={{
          mb: 0,
          borderRadius: 2,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(primario, 0.04)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(primario, 0.12), color: primario, width: 40, height: 40 }}>
              <SettingsEthernet sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                Monitoramento de CTOs
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                Visualize o status operacional das caixas de terminacao optica em tempo real
              </Typography>
            </Box>
          </Box>
          <Box
            display="flex"
            gap={0.5}
            alignItems="center"
            sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 420 }, flex: { xs: 1, md: 'unset' } }}
          >
            <Autocomplete
              size="small"
              options={ctosWithCoords}
              getOptionLabel={(option) => option.name}
              inputValue={searchInput}
              onInputChange={(_, value) => setSearchInput(value)}
              onChange={(_, value) => handleSearchSelect(value)}
              filterOptions={(options, { inputValue }) =>
                options.filter(option =>
                  option.name.toLowerCase().includes(inputValue.trim().toLowerCase())
                )
              }
              noOptionsText="Nenhuma CTO encontrada"
              sx={{ flex: 1, minWidth: 0 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Pesquisar CTO pelo nome..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Search sx={{ ml: 0.5, mr: 0.5, color: 'action.active', fontSize: 18 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    bgcolor: 'background.paper',
                    '& .MuiOutlinedInput-root': { height: 32 },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.25 }}>
                    <Circle sx={{ fontSize: 10, color: getStatusColor(option.cto_status) }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {normalizeStatusLabel(option.cto_status)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={loadData}
              startIcon={<Refresh sx={{ fontSize: 18 }} />}
              disabled={loading}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Atualizar Mapa
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Total no mapa"
            value={stats.total}
            icon={<Hub />}
            active={activeFilter === 'Todos'}
            onClick={() => handleFilterChange('Todos')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Online"
            value={stats.online}
            color={STATUS_COLORS.online}
            icon={<CheckCircle />}
            active={activeFilter === 'Online'}
            onClick={() => handleFilterChange('Online')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Offline"
            value={stats.offline}
            color={STATUS_COLORS.offline}
            icon={<Warning />}
            active={activeFilter === 'Offline'}
            onClick={() => handleFilterChange('Offline')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Vazia"
            value={stats.vazia}
            color={STATUS_COLORS.vazia}
            icon={<Circle />}
            active={activeFilter === 'Vazia'}
            onClick={() => handleFilterChange('Vazia')}
          />
        </Grid>
      </Grid>

      {/* Map */}
      <Paper
        elevation={3}
        sx={{
          flex: 1,
          minHeight: 520,
          overflow: 'hidden',
          borderRadius: 3,
          position: 'relative',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        {/* Map overlay: active filter + legend */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            pointerEvents: 'none'
          }}
        >
          <Paper
            elevation={4}
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 2,
              pointerEvents: 'auto',
              backdropFilter: 'blur(8px)',
              bgcolor: alpha(theme.palette.background.paper, 0.92)
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" sx={{ mb: 0.75 }}>
              <FilterList fontSize="small" color="action" />
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                Filtro: {FILTER_OPTIONS.find(f => f.key === activeFilter)?.label}
              </Typography>
              <Chip label={filteredCtos.length} size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }} />
            </Stack>
            <Stack direction="row" spacing={1.5} justifyContent="center">
              {FILTER_OPTIONS.filter(f => f.key !== 'Todos').map(f => (
                <Stack key={f.key} direction="row" spacing={0.5} alignItems="center">
                  <Circle sx={{ fontSize: 10, color: f.color }} />
                  <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Box>

        <Wrapper apiKey={googleMapsApiKey}>
          <MapComponent
            ctos={filteredCtos}
            onCtoClick={handleOpenCtoDetails}
            isDarkMode={theme.palette.mode === 'dark'}
            focusTarget={focusTarget}
          />
        </Wrapper>
      </Paper>

      <CtoMonitoramentoDetailsModal
        open={!!selectedCto}
        caixaId={selectedCto?.id ?? null}
        initialCto={selectedCto}
        onClose={handleCloseCtoDetails}
      />

    </Box>
  );
};

export default MonitoramentoCtosPage;
